import React, { useContext, useEffect, useState } from "react";
import { OrganizeContext } from "../organize-context";
import {
  concat,
  descend,
  equals,
  isEmpty,
  last,
  lensProp,
  mergeRight,
  prop,
  sort,
  uniqBy,
  view,
} from "ramda";
import { useSearchQuery } from "../../common/use-searchQuery";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { notBlank } from "../../../util/ext";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";

import Empty from "../common/Empty";
import MediaTypeBadge from "../../common/MediaTypeBadge";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import { useMediaPermissions } from "../../../util/mediaUtil";

const initialContentGroupsState = {
  page: 0,
  limit: 30,
  orderBy: "auditUpdated",
  orderDirection: "desc",
};

const sortByLastUpdated = sort(descend(prop("auditUpdated")));

const ContentGroupListItem = ({
  group,
  handleGroupCheckboxClick,
  selectedGroup,
  addBreadcrumb,
}) => {
  const { readOnly } = useMediaPermissions(group);
  const { moveNotAllowed } = useContext(OrganizeContext);
  return (
    <ListItem
      button
      disabled={
        moveNotAllowed({
          group,
        }) || readOnly
      }
      key={group.id}
      onClick={handleGroupCheckboxClick(group)}
    >
      <ListItemIcon>
        <Checkbox
          checked={equals(selectedGroup.id, group.id)}
          disabled={
            moveNotAllowed({
              group,
            }) || readOnly
          }
          color="primary"
        />
      </ListItemIcon>
      <ListItemText>{group.name}</ListItemText>
      <MediaTypeBadge group={group} />
      <ListItemSecondaryAction>
        <IconButton edge="end" onClick={addBreadcrumb(group)} size="large">
          <FontAwesomeIcon icon={faAngleRight} />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const ContentGroupsList = props => {
  const {
    addBreadcrumb,
    breadcrumbs,
    handleGroupCheckboxClick,
    query,
    selectedGroup,
  } = props;

  const contentGroupsQuery = useSearchQuery(
    mergeRight(
      initialContentGroupsState,
      view(lensProp("state"), last(breadcrumbs)),
    ),
  );

  const [contentGroupsQueryState, setContentGroupsQueryState] = useState({
    limit: initialContentGroupsState.limit,
    offset: contentGroupsQuery.state.limit * contentGroupsQuery.state.page,
    sort: `${contentGroupsQuery.state.orderBy} ${contentGroupsQuery.state.orderDirection}`,
    mediaType: contentGroupsQuery.state.mediaType,
    q: query,
  });

  useEffect(() => {
    const { limit, page } = contentGroupsQuery.state;
    setContentGroupsQueryState(prev => ({
      ...prev,
      offset: limit * page,
      q: query,
    }));
  }, [contentGroupsQuery.state, query]);

  const contentGroupsResult = last(breadcrumbs).useContentGroups(
    contentGroupsQueryState,
  );
  const [contentGroups, setContentGroups] = useState([]);
  useEffect(() => {
    if (contentGroupsResult.isSuccess) {
      setContentGroups(prev => {
        const previous = contentGroupsQuery.state.page === 0 ? [] : prev;
        return sortByLastUpdated(
          uniqBy(
            prop("id"),
            concat(previous, contentGroupsResult.data.contents),
          ),
        );
      });
    }
  }, [
    contentGroupsQuery.state,
    contentGroupsResult.isSuccess,
    contentGroupsResult.data,
  ]);

  if (contentGroupsResult.isSuccess && isEmpty(contentGroups)) {
    return (
      <Empty
        message={
          notBlank(query) ? `No results for ${query}` : "No groups available"
        }
      />
    );
  }

  return (
    <List dense disablePadding>
      {contentGroupsResult.isLoading && <PanelLoadingIndicator />}

      {notBlank(contentGroups) &&
        contentGroups.map(group => (
          <ContentGroupListItem
            key={group.id}
            group={group}
            selectedGroup={selectedGroup}
            addBreadcrumb={addBreadcrumb}
            handleGroupCheckboxClick={handleGroupCheckboxClick}
          />
        ))}

      {contentGroupsResult.isSuccess && contentGroupsResult.data.hasMore && (
        <Button
          style={{ width: "100%" }}
          onClick={() =>
            contentGroupsQuery.handleUpdatePage(
              contentGroupsQuery.state.page + 1,
            )
          }
        >
          More
        </Button>
      )}
    </List>
  );
};

export default ContentGroupsList;
